/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { produce } from "immer";

import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import { resetRefs } from "../../lib/refs/refs";
import {
  VerificationService,
  HybridIdentityPersonalInfoViewModel,
  HybridIdentityPersonalInfoResponse,
  FormSelectChoice,
  State,
  FormFieldConfig,
} from "../../lib/types/types";
import {
  getAvailableStateChoices,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";

import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { CityComponent } from "../FormFields/City/City";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { DriverLicenseNumberComponent as DriverLicenseNumber } from "../FormFields/DriverLicenseNumber/DriverLicenseNumberComponent";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";

interface StepHybridIdentityPersonalInfoComponentProps {
  intl: any;
  verificationService: VerificationService;
}

export const StepHybridIdentityPersonalInfo = ({
  intl,
  verificationService,
}: StepHybridIdentityPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as HybridIdentityPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as HybridIdentityPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const locale = viewModel.localeChoice.value;

  const updateIdentityViewModel = (key: keyof HybridIdentityPersonalInfoViewModel, value: any) => {
    const nextState: HybridIdentityPersonalInfoViewModel = produce(
      viewModel,
      (draft: HybridIdentityPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  // Refs should be reset to ensure the field order isn't affected by the previous step
  resetRefs();

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="hybrid-identity"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />

      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          onChange={(newValue) => {
            updateIdentityViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          onChange={(newValue) => {
            updateIdentityViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>

      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Personal email address is recommended"
          />
        }
        onChange={(newValue) => {
          updateIdentityViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateIdentityViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
        />
      ) : null}
      <BirthDate
        isErrored={!!fieldValidationErrors.birthDate}
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        onChange={(newValue) => {
          updateIdentityViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
      />
      <AddressComponent
        isErrored={!!fieldValidationErrors.address1}
        errorId={fieldValidationErrors.address1}
        onChange={(newValue) => {
          updateIdentityViewModel("address1", newValue);
          updateFieldValidationErrorsByFieldId("address1", newValue, verificationService);
        }}
        value={viewModel.address1}
        isRequired
      />
      <div className="sid-thirds">
        <CityComponent
          isErrored={!!fieldValidationErrors.city}
          errorId={fieldValidationErrors.city}
          onChange={(newValue) => {
            updateIdentityViewModel("city", newValue);
            updateFieldValidationErrorsByFieldId("city", newValue, verificationService);
          }}
          value={viewModel.city}
          isRequired
        />
        <StateSelectComponent
          isRequired
          options={getAvailableStateChoices(verificationService.programTheme, intl)}
          value={viewModel.state || undefined}
          isErrored={Boolean(fieldValidationErrors.state)}
          onChange={async (stateChoice: FormSelectChoice<State, string>) => {
            const stateValue = stateChoice ? stateChoice.value : undefined;
            updateIdentityViewModel("state", stateValue);
            updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
          }}
        />
        <PostalCodeComponent
          isErrored={!!fieldValidationErrors.postalCode}
          onChange={(newValue) => {
            updateIdentityViewModel("postalCode", newValue);
            updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
          }}
          value={viewModel.postalCode}
        />
      </div>
      <DriverLicenseNumber
        value={viewModel.driverLicenseNumber}
        isErrored={!!fieldValidationErrors.driverLicenseNumber}
        isRequired
        onChange={(newValue) => {
          updateIdentityViewModel("driverLicenseNumber", newValue);
          updateFieldValidationErrorsByFieldId(
            "driverLicenseNumber",
            newValue,
            verificationService,
          );
        }}
      />

      {getOptions().customFormFields.map((config: FormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}

      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateIdentityViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
      />

      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectHybridIdentityPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage id="verifyMyIdentityStatus" defaultMessage="Verify My Identity" />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepHybridIdentityPersonalInfoComponent = injectIntl(StepHybridIdentityPersonalInfo);
